'use client';

import { useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  ArchiveTelegramProductDtoReasonEnum,
  TelegramProductExtendedDto,
  useTelegramProductsUserArchive,
  useTelegramProductsUserDelete,
} from '@/generated';
import { useModals } from '@/contexts/ModalContext';
import { useUserData } from '@/contexts/LoginContext';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { notify } from '@/lib/notify';
import { BrandIcon } from '../BrandIcon';
import { MonetizationModal } from '@/modals/MonetizationModal';
import { ResponsiveActionsMenu } from '../ProductsList/MyProducts/ResponsiveMenu';
import { useRouter } from '@/hooks/useRouter';
import { BeforeRemovingProductModal } from '@/modals/BeforeRemovingProductModal';
import { AfterArchivingModal } from '@/modals/AfterArchivingModal';
import { ProductStoppingModal } from '@/modals/ProductStoppingModal';
import { Skeleton } from '@/components/ui/skeleton';
import { useProductContactActions } from '@/hooks/useProductContactActions';

type Props = {
  product: TelegramProductExtendedDto;
};

export const ContactButtons = ({ product }: Props) => {
  const t = useTranslations();

  const { open, close } = useModals();
  const router = useRouter();
  const userData = useUserData();
  const { onClickWrite, onClickCall, guestLoading } = useProductContactActions(product);

  const { mutateAsync: productArchive } = useTelegramProductsUserArchive({
    client: {
      handle401: false,
    },
  });

  const [isUpdating, setIsUpdating] = useState(false);

  const { mutateAsync: productDelete } = useTelegramProductsUserDelete({
    client: {
      handle401: false,
    },
  });

  const deleteProduct = useCallback(
    async (productId: string) => {
      open(
        'before-removing-product',
        <BeforeRemovingProductModal
          onAcceptRemove={async () => {
            setIsUpdating(true);
            try {
              await productDelete({
                id: productId,
              });
            } catch {
              setIsUpdating(false);
              notify.error(t('toasts.generic_error'));
              return;
            }
            notify.success(t('toasts.product_deleted'));
            router.push(`/my/products`);
            setIsUpdating(false);
          }}
        />,
        true,
        'md:h-unset h-unset absolute md:static bottom-0 rounded-b-none md:rounded-b-[24px] m-0',
      );
    },
    [productDelete, open, router, t],
  );

  const archiveProduct = useCallback(
    async (productId: string) => {
      setIsUpdating(true);
      try {
        await productArchive({
          data: {
            id: productId,
          },
        });
      } catch {
        setIsUpdating(false);
        notify.error(t('toasts.generic_error'));
        return;
      }

      setIsUpdating(false);

      open(
        'after-archiving-modal',
        <AfterArchivingModal
          onOk={() => {
            close('after-archiving-modal');
            window.location.reload();
          }}
        />,
        true,
        'md:h-unset h-unset absolute md:static bottom-0 rounded-b-none md:rounded-b-[24px] m-0',
      );
    },
    [productArchive, close, open, t],
  );

  const isOwnProduct = product.telegramUserId === userData?.id;
  const isArchived = product.status === 'Archived';

  const shouldHideButtons = isArchived && !isOwnProduct;

  const goToEdit = useCallback(() => {
    router.push(`/product/edit/${product.link}`);
  }, [router, product]);

  const goToPromote = useCallback(() => {
    open(
      'monetizatiton-modal',
      <MonetizationModal
        productId={product.id}
        onOk={() => {
          close('monetizatiton-modal');
          window.location.reload();
        }}
      />,
      true,
      'md:h-unset h-unset absolute md:static my-0 top-0 bottom-0 md:top-unset  md:my-[30px]',
    );
  }, [close, product, open]);

  const onStopProduct = useCallback(() => {
    const onSaveReason = async (reason: ArchiveTelegramProductDtoReasonEnum, text?: string) => {
      close('archiving-reason-modal');
      setIsUpdating(true);
      try {
        await productArchive({
          data: {
            id: product.id,
            reason: reason,
            otherReason: text,
          },
        });
      } catch {
        setIsUpdating(false);
        notify.error(t('toasts.generic_error'));
        return;
      }

      open(
        'after-archiving-modal',
        <AfterArchivingModal
          onOk={() => {
            close('after-archiving-modal');
            window.location.reload();
          }}
        />,
        true,
        'md:h-unset h-unset absolute md:static bottom-0 rounded-b-none md:rounded-b-[24px] m-0',
      );
      setIsUpdating(false);
    };

    open(
      'archiving-reason-modal',
      <ProductStoppingModal onSave={onSaveReason} />,
      true,
      'md:h-unset h-unset absolute md:static bottom-0 rounded-b-none md:rounded-b-[24px] m-0',
    );
  }, [open, close, productArchive, product, t]);

  const mainAction = useMemo(() => {
    if (!isOwnProduct) {
      return;
    }
    const hasPromote = product?.actions?.includes('PROMOTE');

    if (hasPromote) {
      return {
        action: goToPromote,
        text: t('product.promote_product'),
      };
    }

    return {
      action: goToEdit,
      text: t('product.edit'),
    };
  }, [isOwnProduct, product, t, goToPromote, goToEdit]);

  const actions = useMemo(() => {
    return (product?.actions || []).map((status) => {
      switch (status) {
        case 'EDIT':
          return {
            key: 'edit',
            label: product.status === 'Archived' ? t('product.edit_and_publish') : t('my_products.edit'),
            icon: <Image src='/assets/icons/edit-mp.svg' width={24} height={24} alt='' />,
            onSelect: goToEdit,
          };
        case 'PROMOTE':
          return {
            key: 'top_up_views',
            label: t('my_products.top_up_views'),
            icon: <Image src='/assets/icons/topup-mp.svg' width={24} height={24} alt='' />,
            onSelect: goToPromote,
          };
        case 'UNPUBLISH':
          return {
            key: 'unpublish',
            label: t('my_products.unpublish'),
            icon: <Image src='/assets/icons/archive-active.svg' width={24} height={24} alt='' />,
            onSelect: onStopProduct,
          };
        case 'DELETE':
          return {
            key: 'delete',
            label: t('my_products.delete'),
            icon: <Image src='/assets/icons/close-green.svg' width={24} height={24} alt='' />,
            onSelect: () => {
              deleteProduct(product.id);
            },
          };
        case 'ARCHIVE':
          return {
            key: 'send_to_archive',
            label: t('my_products.send_to_archive'),
            icon: <Image src='/assets/icons/archive-mp.svg' width={24} height={24} alt='' />,
            onSelect: () => {
              archiveProduct(product.id);
            },
          };
      }
      return {
        key: 'edit',
        label: t('my_products.edit'),
        icon: <Image src='/assets/icons/edit-mp.svg' width={24} height={24} alt='' />,
        onSelect: goToEdit,
      };
    });
  }, [product, t, goToEdit, onStopProduct, goToPromote, deleteProduct, archiveProduct]);

  const contactButtons = (
    <>
      <Button
        variant='default'
        onClick={onClickCall}
        disabled={guestLoading !== null}
        className='lg:px-[18px] w-[49%] h-auto gap-[8px] px-[10px] py-[10px] rounded-[12px] font-medium text-[16px] leading-[36px] text-white'
      >
        <Image src='/assets/icons/icon-call-to.svg' width={16} height={16} alt='' />
        <span>{t('product.callBtn')}</span>
      </Button>
      <Button
        variant='default'
        onClick={onClickWrite}
        disabled={guestLoading !== null}
        className='lg:px-[18px] w-[49%] h-auto gap-[8px] px-[10px] py-[10px] rounded-[12px] font-medium text-[16px] leading-[36px] text-white bg-black hover:bg-black'
      >
        <Image src='/assets/icons/type_message.svg' width={16} height={16} alt='' className='brightness-0 invert' />
        {t('product.writeBtn')}
      </Button>
    </>
  );

  const actionButtons = (
    <>
      {isUpdating ? (
        <Skeleton className='w-[180px] h-[42px] rounded-[12px]' />
      ) : (
        <Button
          variant='default'
          onClick={mainAction?.action}
          className='lg:px-[18px] grow-2 h-auto px-[10px] py-[10px] rounded-[12px] font-medium text-[16px] leading-[36px] text-white'
        >
          {mainAction?.text}
        </Button>
      )}
      <ResponsiveActionsMenu
        actions={actions}
        trigger={
          <Button
            variant='secondary'
            className='md:w-[56px] md:h-[56px] w-[56px] h-[56px] bg-primary-stroke-day hover:bg-primary-stroke-day md:rounded-[12px] rounded-[4px] border-pr'
            aria-label='Открыть меню'
          >
            <BrandIcon src='/assets/icons/three-dots.svg' size={20} />
          </Button>
        }
      />
    </>
  );

  if (shouldHideButtons) {
    return null;
  }

  return (
    <>
      <div className=' gap-[4px] hidden lg:flex'>{isOwnProduct ? actionButtons : contactButtons}</div>

      <div className='flex gap-[4px] w-full flex-row lg:hidden'>{isOwnProduct ? actionButtons : contactButtons}</div>
    </>
  );
};
